import { useMemo } from 'react'
import { useLoaderData } from '@remix-run/react'

import { getArticlesByCategory } from '~/queries/getArticle'
import { BlogPosts } from '~/components/blog/BlogPosts'
import { BlogPaginate } from '~/components/blog/BlogPaginate'
import { Blog } from '~/components/blog/Blog'
import { Layout } from '~/components/Layout'
import { getCategories } from '~/queries/getCategories'
import { groupPosts } from '~/components/blog/BlogPosts/utils'

import type { LoaderFunction, MetaFunction } from '@remix-run/node'
import type { EntryCollection } from 'contentful'
import {
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
} from '~/utils/routes/routeHelper'
import { TypeArticleSkeleton, TypeCategorySorting } from '~/types/contentful'

export type CareerAdvicePageDataType = {
  articleEntries: EntryCollection<TypeArticleSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  categoryEntries: TypeCategorySorting<'WITHOUT_UNRESOLVABLE_LINKS', string>
  page: number
}

export const loader: LoaderFunction = async ({ request }) => {
  const url = prepareRemixRequest(request)

  const contextValue = preparePageContext(url)
  const page = contextValue.url.searchParams['page']

  const [articleEntries, categoryEntries] = await Promise.all([
    getArticlesByCategory({
      page: Number(page || '1'),
      perPage: 15,
    }),
    getCategories(),
  ])

  return {
    articleEntries,
    categoryEntries: categoryEntries?.items[0] || [],
    page,
    contextValue,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const metaRobots = prepareRobotsMetadata(data.contextValue.url)
  return [
    { title: 'Career advice on writing resumes, acing job interviews and more' },
    {
      name: 'description',
      content:
        'Our experts have all the career advice you need to succeed! Read Career.io´s articles and gain a competitive edge in today´s fast-paced workforce.',
    },
    { name: 'robots', content: metaRobots },
  ]
}

export default function BlogPage() {
  const { articleEntries, categoryEntries } = useLoaderData<
    typeof loader
  >() as CareerAdvicePageDataType
  const postGroups = useMemo(() => groupPosts(articleEntries.items), [articleEntries.items])
  const articleEntry = articleEntries.items[0]
  const { color } = articleEntry.fields

  return (
    <Layout headerBgColor={color === null ? '' : color?.toString()}>
      <Layout.Content>
        <Blog categories={categoryEntries}>
          <BlogPosts postGroups={postGroups} categories={categoryEntries} />
          <BlogPaginate total={articleEntries.total} perPage={15} />
        </Blog>
      </Layout.Content>
    </Layout>
  )
}
